import React, { useCallback, useMemo } from 'react'
import styles from './matchDetails.module.scss'
import i18n from 'constants/strings'
import { useFormikContext } from 'formik'
import { FormState as PostScoreFormState, ScoreValidationError } from 'forms/post-score/types'
import { MatchUpTypeEnum, SideEnum } from 'types/api/enums'
import MatchScoreInput from './score-input/MatchScoreInput'
import PlayerCard from './PlayerCard'
import { useModalDispatch, useModalState, withModals } from 'hooks/use-modal'
import AddPlayerModal from './modals/AddPlayerModal'
import { PostScoreModalId } from 'constants/modals'

export const modalsState = {
  modals: {
    [PostScoreModalId.ADD_PLAYER]: {
      isOpen: false,
      scorePlayerKey: '',
      selectedPlayersMap: {} as Record<string, boolean>,
    },
  },
}

export type PostScoreModalsState = typeof modalsState.modals

const ErrorMessage = ({ error }: { error: ScoreValidationError }) => (
  <p className={styles.errorMsg}>
    <em>
      {error.setType === 'superTiebreak'
        ? `${i18n.t('strSocialMatches.postScore.matchScore.label.superTiebreak')}: `
        : `${i18n.t('strSocialMatches.postScore.matchScore.label.set')} ${error.setIndex + 1}: `}
    </em>
    {error.message}
  </p>
)

const MatchDetails = () => {
  const {
    values: {
      type,
      sides: { SIDE1, SIDE2 },
      score: { matchWinner },
      scoreErrors,
    },
    setFieldValue,
  } = useFormikContext<PostScoreFormState>()
  const sideRef =
    type === MatchUpTypeEnum.SINGLES
      ? i18n.t('strSocialMatches.postScore.matchScore.playerCard.sideRef.singles')
      : i18n.t('strSocialMatches.postScore.matchScore.playerCard.sideRef.doubles')
  const { modalDispatch, modalActions } = useModalDispatch<
    PostScoreModalsState,
    PostScoreModalId.ADD_PLAYER
  >(PostScoreModalId.ADD_PLAYER)
  const { scorePlayerKey } = useModalState<PostScoreModalsState, PostScoreModalId.ADD_PLAYER>(
    PostScoreModalId.ADD_PLAYER
  )
  const createHandlers = useCallback(
    (playerKey: string) => ({
      handleClickAdd: () => {
        modalDispatch(modalActions.setValue({ scorePlayerKey: playerKey }))
        modalDispatch(modalActions.openModal())
      },
      handleClickClear: () => {
        setFieldValue(`sides.${playerKey}`, null)
      },
    }),
    [modalDispatch, modalActions, setFieldValue]
  )

  const [handlersPlayer2, handlersPlayer3, handlersPlayer4] = useMemo(() => {
    return ['SIDE1.player2', 'SIDE2.player1', 'SIDE2.player2'].map(key => createHandlers(key))
  }, [createHandlers])

  const selectedPlayersIdsMap = useMemo(() => {
    let idsMap: Record<string, boolean> = {}
    ;[SIDE1, SIDE2].forEach(side => {
      Object.values(side).forEach(player => {
        if (player) idsMap[player.id] = true
      })
    })

    return idsMap
  }, [SIDE1, SIDE2])

  const handleSelectPlayer = useCallback(
    player => {
      setFieldValue(`sides.${scorePlayerKey}`, player)
      modalDispatch(modalActions.closeModal())
    },
    [setFieldValue, scorePlayerKey, modalDispatch, modalActions]
  )

  return (
    <>
      <div className={styles.container}>
        <PlayerCard
          player={SIDE1.player1}
          sideRef={`${sideRef} 1`}
          hasWon={matchWinner === SideEnum.SIDE1}
          isClearable={false}
        />
        {type === MatchUpTypeEnum.DOUBLES && (
          <PlayerCard
            player={SIDE1.player2}
            sideRef={`${sideRef} 1`}
            hasWon={matchWinner === SideEnum.SIDE1}
            onClickAdd={handlersPlayer2.handleClickAdd}
            onClickClear={handlersPlayer2.handleClickClear}
          />
        )}
        <div className={styles.scoreInputContainer}>
          <MatchScoreInput />
        </div>
        <PlayerCard
          player={SIDE2.player1}
          sideRef={`${sideRef} 2`}
          hasWon={matchWinner === SideEnum.SIDE2}
          onClickAdd={handlersPlayer3.handleClickAdd}
          onClickClear={handlersPlayer3.handleClickClear}
        />
        {type === MatchUpTypeEnum.DOUBLES && (
          <PlayerCard
            player={SIDE2.player2}
            sideRef={`${sideRef} 2`}
            hasWon={matchWinner === SideEnum.SIDE2}
            onClickAdd={handlersPlayer4.handleClickAdd}
            onClickClear={handlersPlayer4.handleClickClear}
          />
        )}
      </div>
      <div className={styles.errorsContainer}>
        {scoreErrors.map((err, i) => (
          <ErrorMessage key={i} error={err} />
        ))}
      </div>
      <AddPlayerModal
        selectedPlayersIdsMap={selectedPlayersIdsMap}
        onSelectPlayer={handleSelectPlayer}
      />
    </>
  )
}

export default withModals(MatchDetails, modalsState)
